import React from 'react'
import { css } from '@emotion/react'
import Link from 'next/link'
import { Card, Column } from 'plume-ui'
import {
  CustomLinkData,
  IPOData,
  IPOShareholderData,
  PreIPOData,
} from '../../../../../types/api'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import { DoubleColumnContent } from '../../../investor/DoubleColumnContent'
import { DescriptionList } from '../../../../atoms/DescriptionList'
import { formatWithComma } from '@/lib/number'
import { INVESTOR_HREF } from '@/lib/url'
import { A } from '../../../../atoms/A'
import {
  SubLabel,
  Table,
  TableCell,
  TableHeading,
} from '../../../../atoms/Table'
import { Help } from '../../../../common/Help'
import { EmptyCard } from '../../../../common/EmptyCard'

type Props = {
  ipoData: IPOData
  preIPOData: PreIPOData
  ipoShareholders: IPOShareholderData[]
  customLink: CustomLinkData
}

export const CompanyIPOData: React.FC<Props> = (props) => {
  return (
    <>
      <IPOList ipoData={props.ipoData} />
      <PreIPOList preIPOData={props.preIPOData} />
      <IPOShareholdersTable ipoShareholders={props.ipoShareholders} />
      <CustomLinkList customLink={props.customLink} />
    </>
  )
}

const IPOList: React.FC<{ ipoData: IPOData }> = (props) => {
  return (
    <Card>
      <CardInnerTitle title="IPO時データ" />
      <DoubleColumnContent>
        <DescriptionList
          css={css`
            dt,
            dd {
              flex: 1;
            }
          `}
          list={{
            初値: { text: formatWithComma(props.ipoData.initialPrice) },
            公募価格: {
              text: formatWithComma(props.ipoData.publicOfferingPrice),
            },
            初値の騰落率: {
              text: inPercentage(props.ipoData.initialPriceRiseFallRate),
            },
            'IPO時 資金調達金額: 公募(百万円)': {
              text: formatWithComma(props.ipoData.ipoFundraising, {
                decimalPlace: 1,
              }),
              help: 'IPO時の公募による資金調達の総額',
            },
            'IPO時 時価総額(初値)(百万円)': {
              text: formatWithComma(props.ipoData.ipoMarketCap, {
                decimalPlace: 1,
              }),
              help: 'IPO時の初値×発行済株式数（※潜在株は含まない）',
            },
            初値PER: {
              text: formatWithComma(props.ipoData.per, { decimalPlace: 2 }),
            },
            'IPO日前 株数': {
              text: formatWithComma(props.ipoData.numberOfSharesBeforeIpo),
            },
            'IPO日 株数': {
              text: formatWithComma(props.ipoData.numberOfSharesAtIpo),
            },
            'IPOまでの年数(年)': {
              text: formatWithComma(props.ipoData.yearsToIpo, {
                decimalPlace: 2,
              }),
            },
            'IPO企業 社長名': {
              text: props.ipoData.ipoRepresentative,
            },
            社長生年月日: {
              text: props.ipoData.representativeBirthday,
            },
            社長年齢: {
              text: props.ipoData.representativeAge,
            },
            'IPO前期 売上: 単体(百万円)': {
              text: formatWithComma(
                props.ipoData.previousTermSalesNonConsolidated,
                { decimalPlace: 1 }
              ),
              help: 'IPO直前期の売上',
            },
            'IPO前期 売上: 連結(百万円)': {
              text: formatWithComma(
                props.ipoData.previousTermSalesConsolidated,
                { decimalPlace: 1 }
              ),
              help: 'IPO直前期の売上',
            },
            'IPO前期 純利益: 単体(百万円)': {
              text: formatWithComma(
                props.ipoData.previousTermNetIncomeNonConsolidated,
                { decimalPlace: 1 }
              ),
              help: 'IPO直前期の純利益',
            },
            'IPO前期 純利益: 連結(百万円)': {
              text: formatWithComma(
                props.ipoData.previousTermNetIncomeConsolidated,
                { decimalPlace: 1 }
              ),
              help: 'IPO直前期の純利益',
            },
          }}
        />
        <DescriptionList
          css={css`
            dt,
            dd {
              flex: 1;
            }
          `}
          list={{
            主幹事: {
              text: props.ipoData.leadScretary,
            },
            監査法人: {
              text: props.ipoData.auditFirm,
            },
            ロックアップ期間: {
              text: props.ipoData.lockupPeriod,
            },
            ラウンド数: {
              text: props.ipoData.numberOfRounds,
            },
            VC数: {
              text: props.ipoData.numberOfVCs,
            },
            VC比率: {
              text: inPercentage(props.ipoData.vcPercentage),
            },
            '事業会社 比率': {
              text: inPercentage(props.ipoData.cvcPercentage),
            },
            '個人 比率': {
              text: inPercentage(props.ipoData.angelPercentage),
            },
            '潜在株 比率': {
              text: inPercentage(props.ipoData.dilutiveSharesPercentage),
            },
            '社員 持株会': {
              text: inPercentage(props.ipoData.employeeShareholdingAssociation),
              help: '従業員持株会の株式総数に対する\n所有株式数の割合(潜在株を含む)',
            },
            'リード（トップ）VC': {
              text: props.ipoData.leadVC && (
                <Link
                  href={INVESTOR_HREF}
                  as={props.ipoData.leadVC.path}
                  passHref
                >
                  <A decoration="none">{props.ipoData.leadVC.name}</A>
                </Link>
              ),
            },
            シェア: {
              text: inPercentage(props.ipoData.leadVCShare),
            },
            'VC 比率：備考': {
              text: props.ipoData.vcRemarks,
            },
            '創業者 個人所有': {
              text: inPercentage(props.ipoData.founderPersonalShareHoldingRate),
              help: '創業者(個人名)の株式総数に対する\n所有株式数の割合(潜在株を含む)',
            },
            '創業者 一族所有': {
              text: inPercentage(props.ipoData.founderFamilyShareHoldingRate),
              help: '創業者、創業者の配偶者および二親等内の\n株式総数に対する所有株式数の割合(潜在株を含む)',
            },
            '創業者 持株会社': {
              text: inPercentage(props.ipoData.founderCompanyHoldingRate),
              help: '創業者の持株会社(財産保全会社)の株式総数に対する\n所有株式数の割合(潜在株を含む)',
            },
            創業者持株比率の備考: {
              text: props.ipoData.founderHoldingRateRemarks,
            },
          }}
        />
      </DoubleColumnContent>
    </Card>
  )
}

const PreIPOList: React.FC<{ preIPOData: PreIPOData }> = (props) => {
  return (
    <Card
      css={css`
        margin-top: 30px;
      `}
    >
      <CardInnerTitle title="PreIPOサマリー情報" />
      <DoubleColumnContent>
        <DescriptionList
          css={css`
            dt,
            dd {
              flex: 1;
            }
          `}
          list={{
            '投資総額(千円)': {
              text: formatWithComma(props.preIPOData.preIpoFunding),
              help: '設立からIPOまでに調達した資金のグロス総額 (資本金+資本準備金+減資額)',
            },
            '投資総額VC(千円)': {
              text: formatWithComma(props.preIPOData.preIpoVCFunding),
              help: '投資総額の内訳、VB企業の資金となったVC投資額、移動などは含まない',
              indent: true,
            },
            '投資総額 事業会社(千円)': {
              text: formatWithComma(props.preIPOData.preIpoCVCFunding),
              indent: true,
            },
            '投資総額個人(千円)': {
              text: formatWithComma(props.preIPOData.preIpoAngelFunding),
              indent: true,
            },
            '投資のべ総額(千円)': {
              text: formatWithComma(props.preIPOData.preIpoGrossInvestment),
              help: '設立からIPOまでに投資された総額 (既存株主からの株式の取得を含む)',
            },
            '投資のべ総額VC(千円)': {
              text: formatWithComma(props.preIPOData.preIpoVCGrossInvestment),
              help: '既存株主からの株式の取得を含む投資家の投資総額',
              indent: true,
            },
            '投資のべ総額 事業会社(千円)': {
              text: formatWithComma(props.preIPOData.preIpoCVCGrossInvestment),
              indent: true,
            },
            '投資のべ総額個人(千円)': {
              text: formatWithComma(
                props.preIPOData.preIpoAngelGrossInvestment
              ),
              indent: true,
            },
            'IPO直近 時価総額(百万円)': {
              text: formatWithComma(props.preIPOData.preIpoPostMoney),
              help: 'IPO直前の第三者割当増資のラウンドまたは株主移動におけるPostMoney',
            },
            サマリー備考: {
              text: props.preIPOData.summaryRemarks,
            },
          }}
        />
        <DescriptionList
          css={css`
            dt,
            dd {
              flex: 1;
            }
            dt {
              white-space: nowrap;
            }
            dd {
              padding-left: 12px;
            }
          `}
          list={{
            '短期借入金(千円)': {
              text: formatWithComma(props.preIPOData.shortDebt),
              help: 'IPO直前(四半期ベース)の短期借入金',
            },
            '短期借入金の合計(千円)': {
              text: formatWithComma(props.preIPOData.shortDebtTotal),
              help: 'IPO直前(四半期ベース)の短期借入金、\n１年以内返済予定の長期借入金、\n１年以内償還予定の社債の合計',
              indent: true,
            },
            '１年以内償還予定の社債(千円)': {
              text: formatWithComma(props.preIPOData.returnableCorportateBond),
              help: 'IPO直前(四半期ベース)の１年以内償還予定の社債',
              indent: true,
            },
            '１年以内返済予定の長期借入金(千円)': {
              text: formatWithComma(props.preIPOData.returnableLongDebt),
              help: 'IPO直前(四半期ベース)の１年以内返済予定の長期借入金',
              indent: true,
            },
            '長期借入金(千円)': {
              text: formatWithComma(props.preIPOData.longDebt),
              help: 'IPO直前(四半期ベース)の長期借入金',
            },
            '社債(千円)': {
              text: formatWithComma(props.preIPOData.corporateBond),
              help: 'IPO直前(四半期ベース)の社債',
              indent: true,
            },
            '長期借入金の合計(千円)': {
              text: formatWithComma(props.preIPOData.longDebtTotal),
              help: 'IPO直前(四半期ベース)の長期借入金の合計',
              indent: true,
            },
            '短期および長期借入金の合計(千円)': {
              text: formatWithComma(props.preIPOData.debtTotal),
              help: 'IPO直前(四半期ベース)の短期および長期借入金の合計',
            },
            '株主資本合計(千円)': {
              text: formatWithComma(props.preIPOData.shareholdersEquity),
            },
            '純資産合計(千円)': {
              text: formatWithComma(props.preIPOData.assetValue),
            },
            '負債純資産合計(千円)': {
              text: formatWithComma(
                props.preIPOData.totalLiabilitiesAndNetAssets
              ),
            },
          }}
        />
      </DoubleColumnContent>
    </Card>
  )
}

const IPOShareholdersTable: React.FC<{
  ipoShareholders: IPOShareholderData[]
}> = (props) => {
  return (
    <Card
      css={css`
        margin-top: 30px;
      `}
      id="shareholders"
    >
      <CardInnerTitle title="株主サマリー (IPO時)" />
      {props.ipoShareholders.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <TableHeading>投資家名</TableHeading>
              <TableHeading>
                IPO直前株数<Help>IPO直前の所有株数(潜在株を含む)</Help>
              </TableHeading>
              <TableHeading>
                シェア
                <Help>IPO直前の所有株式(潜在株式を含む)の割合%</Help>
              </TableHeading>
              <TableHeading>
                投資金額合計
                <SubLabel>千円</SubLabel>
                <Help>
                  {`IPOまでに投資した金額(株式を譲受した場合は取得額を\n投資額に加算、譲渡した場合は売却額を投資額から減額)`}
                </Help>
              </TableHeading>
              <TableHeading>
                平均株価
                <SubLabel>円</SubLabel>
                <Help>
                  {`投資金額合計(株式を譲受した場合は取得額を投資額に加算、\n譲渡した場合は売却額を投資額から減額)÷全株数`}
                </Help>
              </TableHeading>
              <TableHeading>備考</TableHeading>
            </tr>
          </thead>
          <tbody>
            {props.ipoShareholders.map((s, i) => (
              <tr key={i}>
                <TableCell>
                  <A href={s.investor.path} decoration="none">
                    {s.investor.name}
                  </A>
                </TableCell>
                <TableCell align="right">
                  {formatWithComma(s.numberOfSharesBeforeIpo, {
                    decimalPlace: 1,
                  })}
                </TableCell>
                <TableCell align="right">
                  {inPercentage(s.share, { decimalPlace: 3 })}
                </TableCell>
                <TableCell align="right">
                  {formatWithComma(s.totalInvestment)}
                </TableCell>
                <TableCell align="right">
                  {formatWithComma(s.stockAverage, { decimalPlace: 2 })}
                </TableCell>
                <TableCell>{s.remarks}</TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard>データ未作成です</EmptyCard>
      )}
    </Card>
  )
}

const CustomLinkList: React.FC<{ customLink: CustomLinkData }> = (props) => {
  const links = Object.entries(props.customLink).filter(
    ([_k, url]) => url !== null
  )

  if (links.length === 0) {
    return null
  }

  const leftLinks = links.slice(0, 3)
  const rightLinks = links.slice(3, 6)

  const linkLabels = {
    tokyoIpo: '東京IPO',
    tradersWeb: "Trader's WEB",
    irData: 'IRデータ（KCR総研）',
    yahooKessan: '連結決算推移',
    yahooCorp: 'Yahoo企業概要',
    nikkeiNet: '日経Net',
  }

  return (
    <Card
      css={css`
        margin-top: 30px;
      `}
    >
      <CardInnerTitle title="カスタムリンク" />
      <Card.Content>
        <Column.Group>
          {[leftLinks, rightLinks].map((links, i) => (
            <Column key={i}>
              {links.map(([k, url], i) => (
                <div
                  key={i}
                  css={css`
                    margin-bottom: 0.3rem;
                  `}
                >
                  <A
                    href={url!}
                    target="_blank"
                    rel="noopener"
                    decoration="none"
                  >
                    {linkLabels[k as keyof CustomLinkData]}
                  </A>
                </div>
              ))}
            </Column>
          ))}
        </Column.Group>
      </Card.Content>
    </Card>
  )
}

const inPercentage = (n: number | null, options?: { decimalPlace: number }) => {
  const decimalPlace = options?.decimalPlace ?? 2
  return n ? `${formatWithComma(n, { decimalPlace })}%` : ''
}
